import { ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { Role } from '../../model/auth/Role';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationHandler } from '../../helper/notification.handler';
import { AvailableContestTasks, Contest } from '../../model/contest/Contest';
import { ContestService } from '../../service/contest.service';
import { ContestTaskService } from '../../service/contest.task.service';
import { User } from '../../model/auth/User';
import { AuthService } from '../../service/auth.service';
import { TenantService } from '../../service/tenant.service';
import { PaymentStatus } from '../../model/Payment';
import {
  Answer,
  ContestTask,
  ContestTaskType,
  ITEM_INDEX_COMPARATOR,
  localizeContestTaskDescription,
  localizeContestTaskType,
  Question,
  Section
} from '../../model/contest/ContestTask';
import { Locale, localizeLocale } from '../../model/Locale';
import {
  ConfirmationContext,
  ConfirmationData,
  ConfirmationModalComponent
} from '../../common/modal/confirmation-modal/confirmation-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { UrlHelper } from '../../helper/url.helper';
import { localizeSchoolGrade, School, SchoolGrade } from '../../model/location/School';
import { MathService } from '../../math/math.service';
import { DeactivationAwareComponent } from '../../common/can-deactivate/deactivation-aware.component';
import { ContestEventData, ContestEventType, ContestProgress } from '../../model/contest/ContestEvent';
import { LocationService } from '../../service/location.service';
import { EventCategory, EventType, TrackingService } from '../../service/tracking.service';
import {
  ClientTimeShiftComponent,
  TimeData
} from '../../common/modal/client-time-shift-modal/client-time-shift.component';
import { Observable, of } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { ErrorInterceptor } from '../../helper/error.interceptor';
import { ContestQuestionComponent } from '../../contest-task/contest-question/contest-question.component';
import { TimerComponent } from '../../common/timer/timer.component';

@Component({
  selector: 'app-contest-exam-center',
  templateUrl: './contest-exam-center.component.html',
  styleUrls: ['./contest-exam-center.component.scss']
})
export class ContestExamCenterComponent extends DeactivationAwareComponent implements OnDestroy {

  readonly roles = [Role.Student, Role.Host];

  @ViewChild('timer', {static: false}) protected timerComponent!: TimerComponent;

  competitionId?: string;
  contest?: Contest | null;
  contestLoadFailed = false;
  enrolledContests?: Contest[];
  availableTasks?: AvailableContestTasks;
  user!: User | null;
  school?: School;

  taskGrade?: SchoolGrade;
  taskType?: ContestTaskType;
  taskLocale?: Locale;
  task?: ContestTask;
  taskLoadFailed = false;
  progress?: ContestProgress;

  answeredQuestions = new Map<string, string[]>();
  selectedQuestion = 0;
  questionsCount = 0;

  sectionQuestionsCount = new Map<number, number>();

  today!: Date;
  clientTimeShift = 0;

  inProgress = false;
  manuallyFinished = false;
  previewMode = false;
  singleOption = false;

  constructor(private contestService: ContestService,
              private contestTaskService: ContestTaskService,
              private tenantService: TenantService,
              private locationService: LocationService,
              private trackingService: TrackingService,
              private authService: AuthService,
              private mathService: MathService,
              private route: ActivatedRoute,
              private router: Router,
              private dialog: MatDialog,
              public sanitizer: DomSanitizer,
              private cdRef: ChangeDetectorRef,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.manuallyFinished = true;
    if(this.timerComponent) this.timerComponent.clearTimer();
  }

  async initContent() {
    this.today = new Date();

    this.route.params.subscribe(params => {
      if (params && params.contestId) {
        const contestId = params.contestId as string;
        this.loadContest(contestId);
      }
    });

    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;

      if (!this.previewMode) {
        this.loadEnrolledContests(this.competitionId);
      }
    }

    this.user = await this.authService.getUser();
    if (this.user && !this.previewMode) {
      this.taskGrade = this.user.schoolGrade;

      if (this.user.schoolId) this.loadSchoolDetails(this.user.schoolId);
    }
  }

  rolesUpdated(roles: Role[]) {
    this.previewMode = !roles.includes(Role.Student);
  }

  canDeactivate(): boolean {
    return !this.task || this.timeIsUp || this.manuallyFinished;
  }

  deactivateWarning(): string {
    return $localize`:Contest Exam|Contest in progress deactivation title@@contest-exam-contest-in-progress-deactivation-title:The contest is in progress, and you can lose any unsaved data if you proceed. Do you wish to continue?`;
  }

  private loadContest(contestId: string) {
    this.inProgress = true;
    this.contest = null;
    this.contestService.getDetails(contestId).subscribe(contest => {
      this.contest = contest;
      this.availableTasks = contest.tasks;

      if (!this.previewMode) {
        this.loadContestProgress(contestId);
      } else {
        this.inProgress = false;
      }
    }, error => {
      NotificationHandler.handleWarning($localize`:Notifications|Contest details fetch has failed message@@notifications-contest-details-fetch-failed-error:Contest details fetch has failed`, error, false);
      this.inProgress = false;
      this.contestLoadFailed = true;
    });
  }

  loadEnrolledContests(competitionId: string) {
    this.enrolledContests = undefined;

    this.contestService.getContests(competitionId).subscribe(contests => {
      this.enrolledContests = contests || [];
    }, error => NotificationHandler.handleError($localize`:Notifications|Contests fetch has failed message@@notifications-contests-fetch-failed-error:Contests fetch has failed`, error));
  }

  private loadContestTask(contestId: string, taskId: string, type: ContestTaskType) {
    if (this.task) return;

    this.inProgress = true;

    this.contestTaskService.getContestTask(contestId, taskId, type).subscribe(task => {
      if (this.previewMode && task) {
        Object.keys(task.sections).forEach(locale => {
          task.sections[locale].forEach(section => {
            section.questions
              .forEach(question => {
                for (const answer of question.answers) {
                  answer.correct = false;
                }
              });
          });
        });
      }

      this.task = task;

      if (this.task && this.taskLocale) this.handleTaskUpdated();

      if (!this.progress) {
        this.loadContestProgress(contestId);
      } else {
        this.restoreQuestionAnswers();
        this.selectedQuestion = 0;
        this.inProgress = false;
      }
    }, error => {
      const code = ErrorInterceptor.getStatusCode(error);

      if (code == 405) {
        NotificationHandler.handleWarning($localize`:Notifications|Contest task details fetch not allowed message@@notifications-contest-task-details-fetch-not-allowed-error:Contest task details can be fetched only within the contest period for enrolled participants`, error, false);
      } else {
        NotificationHandler.handleError($localize`:Notifications|Contest task details fetch has failed message@@notifications-contest-task-details-fetch-failed-error:Contest task details fetch has failed`, error);
      }

      this.taskLoadFailed = true;
      this.inProgress = false;
    });
  }

  private loadContestProgress(contestId: string) {
    this.inProgress = true;

    this.contestTaskService.getContestProgress(contestId).subscribe(progress => {
      if (progress && progress.startedAt) {
        this.taskType = progress.taskType;
        this.answeredQuestions = new Map<string, string[]>();
        const answers = progress.answers;
        if (answers) {
          Object.keys(answers)
            .forEach(questionId => this.answeredQuestions.set(questionId, answers[questionId] || []));
        }

        if (progress.finishedAt) {
          this.manuallyFinished = true;

          this.progress = progress;
          this.task = new ContestTask(<ContestTaskType>this.taskType);
          this.inProgress = false;
        } else {
          if (progress.startedAt && progress.taskId && progress.taskType) {
            this.taskLocale = progress.taskLocale;
            if (!this.taskLocale) this.autoSelectLocale();

            if (this.answeredQuestions && this.answeredQuestions.size > 0) { // restore
              this.trackingService.sendEvent(EventType.contest_exam_restored, EventCategory.contest_exam, this.contest?.id);
            }

            this.progress = progress;

            this.loadContestTask(contestId, progress.taskId, progress.taskType);
          } else {
            this.progress = progress;
          }
        }
      } else {
        this.progress = progress;

        if (this.taskGrade && !this.taskType && this.contest) {
          // try auto-select type
          if (this.contest.tasks && this.contest.tasks[this.taskGrade]) {
            const task = this.contest.tasks[this.taskGrade];
            if (Object.keys(task).length == 1) {
              this.taskType = Object.keys(task)[0] as ContestTaskType;
            }

            if (this.taskType) {
              const locales = this.contest.tasks[this.taskGrade][this.taskType];
              this.singleOption = locales && Object.keys(task).length == 1;
            }
          }
        }


        this.inProgress = false;
      }
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest progress details fetch has failed message@@notifications-contest-progress-details-fetch-failed-error:Contest progress details fetch has failed`, error);
      this.inProgress = false;
    });
  }

  private restoreQuestionAnswers() {
    if (!this.answeredQuestions || !this.task) return;

    const sections = this.task.sections;
    Object.keys(sections).forEach(locale => {
      sections[locale].forEach(section => {
        section.questions
          .filter(question => this.answeredQuestions.has(question.id || ''))
          .forEach(question => {
            const answersData = this.answeredQuestions.get(question.id || '');
            if (answersData) {
              for (const answer of question.answers) {
                answer.correct = answersData.includes(answer.id || '');
              }
            }
          });
      });
    });

    if (this.contestFinished) {
      this.selectedQuestion = this.questionsCount + 1;
    }
  }

  private loadSchoolDetails(schoolId: string) {
    this.locationService.getSchoolDetails(schoolId).subscribe(school => {
      this.school = school;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|School details fetch has failed message@@notifications-school-details-fetch-failed-error:School details fetch has failed`, error);
    });
  }

  get getContestEndTime(): Date {
    const duration = (this.contest?.config?.duration || 0) * 60 * 1000;

    const endByDuration = (this.progress?.startedAt?.getTime() || 0) + duration;
    const endByContest = this.previewMode
      ? endByDuration
      : this.contest?.endsAt?.getTime() || new Date().getTime();

    return new Date(Math.min(endByDuration, endByContest));
  }

  get timeIsUp(): boolean {
    return this.progress != null
      && this.progress.startedAt != null
      && (this.getContestEndTime.getTime() + this.clientTimeShift) <= new Date().getTime();
  }

  get contestFinished(): boolean {
    return this.answeredQuestions != undefined && this.answeredQuestions.size == this.questionsCount;
  }

  private updateQuestionsCount(task: ContestTask, locale: Locale) {
    const sections = task.sections[locale];

    sections.forEach(section => this.sectionQuestionsCount.set(section.index || 0, section.questions.length));

    this.questionsCount = sections
      ? sections.map(section => section.questions.length).reduce((s1, s2) => s1 + s2) || 0
      : 0;
  }

  displayTaskType(type: ContestTaskType | string): string {
    return localizeContestTaskType(type as ContestTaskType);
  }

  displayTaskDescription(type: ContestTaskType | string): string {
    return localizeContestTaskDescription(type as ContestTaskType);
  }

  get userCanTakePart() {
    return this.contest
      && this.enrolledContests
      && this.enrolledContests
        .filter(c => c.id == this.contest?.id)
        .filter(c => c.paymentStatus == PaymentStatus.successful).length > 0;
  }

  localizeLocale(locale: Locale): string {
    return localizeLocale(locale);
  }

  localizeGrade(grade: SchoolGrade): string {
    return localizeSchoolGrade(grade);
  }

  getAvailableLanguages(data: Record<Locale | string, string>): string {
    return Object.keys(data)
      .map(locale => this.localizeLocale(locale as Locale))
      .join(', ') || $localize`:General|No data provided@@general-no-data-provided:[no data]`;
  }

  private autoSelectLocale() {
    if (!this.user || !this.contest || !this.taskType || !this.progress) return;

    if (!this.taskGrade) {
      NotificationHandler.handleError($localize`:Notifications|Contest task grade and type not provided message@@notifications-contest-task-grade-and-type-not-provided-error:Contest task grade and type not provided`);
      return;
    }

    if (!this.contest.tasks
      || !this.contest.tasks[this.taskGrade]
      || !this.contest.tasks[this.taskGrade][this.taskType]
      || !Object.keys(this.contest.tasks[this.taskGrade][this.taskType]).length) {
      NotificationHandler.handleError($localize`:Contest Exam|No tasks available message@@contest-exam-no-tasks-available-message:No tasks available, please contact a contest organizer `);
      return;
    }

    const locales = Object.keys(this.contest.tasks[this.taskGrade][this.taskType]);

    this.taskLocale = locales[0] as Locale;
  }

  get availableLanguages(): Locale[] {
    return this.availableTasks && this.taskType && this.taskGrade
      ? Object.keys(this.availableTasks[this.taskGrade][this.taskType]) as Locale[]
      : [];
  }

  get taskSections(): Section[] {
    return this.task && this.taskLocale ? this.task.sections[this.taskLocale] || [] : [];
  }

  getQuestionLabel(sectionIndex: number | undefined, questionIndex: number | undefined): string {
    let prevCount = 0;
    for (let i = 1; i < (sectionIndex || 0); i++) {
      prevCount += this.sectionQuestionsCount.get(i) || 0;
    }

    return sectionIndex && questionIndex ? (prevCount + questionIndex).toString() : '?';
  }

  private getTaskId(grade: SchoolGrade, type: ContestTaskType): string | undefined {
    let taskId: string | undefined = undefined;

    if (this.availableTasks && this.availableTasks[grade]) {
      const availableLocaleTasks = this.availableTasks[grade][type];
      const locales = Object.keys(availableLocaleTasks);
      if (locales.length > 0 && availableLocaleTasks[locales[0]] != undefined) {
        taskId = availableLocaleTasks[locales[0]];
      }
    }

    return taskId;
  }

  selectTaskType(type: ContestTaskType | string | undefined) {
    if (!this.taskGrade || !type) {
      NotificationHandler.handleError($localize`:Notifications|Contest task grade and type not provided message@@notifications-contest-task-grade-and-type-not-provided-error:Contest task grade and type not provided`);
      return;
    }

    this.taskType = type as ContestTaskType;
  }

  selectTaskLocale(locale: Locale) {
    const data: ConfirmationData = {
      title: $localize`:Contest Exam|Start contest title@@contest-exam-start-contest-title:Contest Start`,
      action: $localize`:General|Start action label@@general-start-action-label:Start`,
      content: $localize`:Contest Exam|Start contest prompt@@contest-exam-start-contest-prompt:You are about to start the contest. When you proceed, there will be no way to get back and change the task type selection. Once started, a timer cannot be stopped, so ensure you are in a quiet location and have enough free time to complete all tasks. The contest can be taken only once.`
    };
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.actionConfirmed.subscribe((context: ConfirmationContext) => {
      this.taskLocale = locale;

      this.saveContestStartOrFinishEvent(ContestEventType.start);

      context.modal.proceedHandled();
    });
  }

  private handleTaskUpdated() {
    if (this.task && this.taskLocale) this.updateQuestionsCount(this.task, this.taskLocale);

    this.renderMath();
  }

  handleQuestionAnswer(section: Section, question: Question, answers: Answer[], questionComponent: ContestQuestionComponent) {
    if (this.task && this.taskType && this.taskLocale) {
      const data: ContestEventData = {
        section_index: section.index || 0,
        locale: this.taskLocale,
        question_id: question.id || '',
        answers: answers.sort(ITEM_INDEX_COMPARATOR).map(a => a.id || ''),
        client_time: new Date().getTime(),
      };
      if (this.school) data.school = {
        id: this.school.id,
        name: this.school.name,
        short_name: this.school.short_name,
        type: this.school.type,
      };
      if (!this.previewMode) {
        this.contestTaskService.saveContestEvent(this.task.id || '', this.taskType, ContestEventType.answer, data).subscribe(() => {
          this.answeredQuestions.set(question.id || '', answers.map(answer => answer.id || ''));
          question.answers.forEach(answer => answer.correct = answers.includes(answer));
          questionComponent.refreshAnswers();

          if (!answers.length) this.answeredQuestions.delete(question.id || '');

          question.answerInProgress = false;

          this.trackingService.sendEvent(EventType.contest_exam_answered, EventCategory.contest_exam, this.contest?.id);
        }, error => {
          const code = ErrorInterceptor.getStatusCode(error);

          if (code == 405) {
            NotificationHandler.handleWarning($localize`:Notifications|Contest progress event save not allowed message@@notifications-contest-progress-event-save-not-allowed-error:Data was not saved! Contest task progress events can be saved only within the contest period by enrolled participants`, error, false);
          } else if (code == 409) {
            NotificationHandler.handleWarning($localize`:Notifications|Contest progress answer event save conflict message@@notifications-contest-progress-answer-event-save-conflict-error:Answer was not saved! The contest answer can be saved only within the contest duration and before it is finished`, error, false);
          } else {
            NotificationHandler.handleError($localize`:Notifications|Contest progress event save failed message@@notifications-contest-progress-event-save-failed-error:Contest progress event save failed`, error);
          }

          const answered = this.answeredQuestions.get(question.id || '') || [];
          question.answers.forEach(answer => answer.correct = answered.includes(answer.id as string));
          if (!answered.length) this.answeredQuestions.delete(question.id || '');
          questionComponent.refreshAnswers();

          this.inProgress = false;
          question.answerInProgress = false;
        });
      } else {
        this.answeredQuestions.set(question.id || '', answers.map(answer => answer.id || ''));
        question.answers.forEach(answer => answer.correct = answers.includes(answer));
        if (!answers.length) this.answeredQuestions.delete(question.id || '');
        questionComponent.refreshAnswers();

        question.answerInProgress = false;
      }
    }
  }

  async exitExam() {
    if ((this.taskType || this.taskLocale || this.task) && !this.timeIsUp && !this.manuallyFinished && !this.taskLoadFailed) return;

    await this.router.navigate([UrlHelper.DEFAULT_AUTH_PATH], {replaceUrl: true});
  }

  handleQuestionChange() {
    this.renderMath();
  }

  private renderMath(): void {
    setTimeout(() => {
      this.mathService.renderPageMath();
    }, 1);
  }

  handleCompleteContest() {
    const data: ConfirmationData = {
      title: $localize`:Contest Exam|Complete contest prompt title@@contest-exam-complete-contest-prompt-title:Complete contest`,
      action: $localize`:General|Complete action label@@general-complete-action-label:Complete`,
      content: $localize`:Contest Exam|Complete contest confirmation details@@contest-exam-complete-contest-confirmation-details:You are about to complete the contest. If you click "Complete", you will not be able to go back and change your answers. Click "Cancel" if you plan to continue working on the contest.`
    };

    if (this.previewMode) {
      data.details = $localize`:Contest Exam|Complete contest confirmation preview details@@contest-exam-complete-contest-confirmation-preview-details:In preview mode, no data is recorded, so finishing the contest will do nothing.`;
    }

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.actionConfirmed.subscribe((context: ConfirmationContext) => {
      if (!this.previewMode) {
        this.saveContestStartOrFinishEvent(ContestEventType.finish);
      }

      context.modal.proceedHandled();
    });
  }

  handleTimerEnded() {
    if (!this.previewMode) {
      this.saveContestStartOrFinishEvent(ContestEventType.finish);

      this.trackingService.sendEvent(EventType.contest_exam_timed_up, EventCategory.contest_exam, this.contest?.id);
    }
  }

  private saveContestStartOrFinishEvent(eventType: ContestEventType) {
    if (!this.taskLocale) return;

    if (this.previewMode && eventType == ContestEventType.start && this.contest && this.taskType) {
      const taskId = this.taskGrade && this.taskType ? this.getTaskId(this.taskGrade, this.taskType) : undefined;
      if (!taskId) return;
      this.progress = new ContestProgress(this.contest.id);
      this.progress.startedAt = new Date();

      this.loadContestTask(this.contest.id, taskId, this.taskType);
    }

    if (eventType == ContestEventType.start && !this.userCanTakePart) return;

    const taskId = this.taskGrade && this.taskType ? this.getTaskId(this.taskGrade, this.taskType) : undefined;
    if (!taskId) return;

    this.inProgress = true;

    const data: ContestEventData = {
      locale: this.taskLocale,
      client_time: new Date().getTime(),
    };
    if (this.school) data.school = {
      id: this.school.id,
      name: this.school.name,
      short_name: this.school.short_name,
      type: this.school.type,
    };

    if (this.taskType) this.contestTaskService.saveContestEvent(taskId, this.taskType, eventType, data).subscribe(() => {
      if (eventType == ContestEventType.start && this.contest && this.taskType) {
        this.trackingService.sendEvent(EventType.contest_exam_started, EventCategory.contest_exam, this.contest.id);

        this.loadContestProgress(this.contest.id);
      } else if (eventType == ContestEventType.finish && !this.timeIsUp) {
        this.manuallyFinished = true;
        this.inProgress = false;

        this.trackingService.sendEvent(EventType.contest_exam_finished, EventCategory.contest_exam, this.contest?.id);
      } else {
        this.inProgress = false;

        if (eventType == ContestEventType.finish) {
          this.trackingService.sendEvent(EventType.contest_exam_finished, EventCategory.contest_exam, this.contest?.id);
        }
      }
    }, error => {
      const code = ErrorInterceptor.getStatusCode(error);

      if (code == 405) {
        NotificationHandler.handleWarning($localize`:Notifications|Contest progress event save not allowed message@@notifications-contest-progress-event-save-not-allowed-error:Data was not saved! Contest task progress events can be saved only within the contest period by enrolled participants`, error, false);
      } else if (code == 409) {
        if (eventType == ContestEventType.start) {
          NotificationHandler.handleWarning($localize`:Notifications|Contest progress start event save conflict message@@notifications-contest-progress-start-event-save-conflict-error:The contest can be started only once`, error, false);
        } else {
          NotificationHandler.handleWarning($localize`:Notifications|Contest progress finish event save conflict message@@notifications-contest-progress-finish-event-save-conflict-error:The contest can be finished only once within the contest duration`, error, false);
        }
      } else {
        NotificationHandler.handleError($localize`:Notifications|Contest progress event save failed message@@notifications-contest-progress-event-save-failed-error:Contest progress event save failed`, error);
      }

      this.inProgress = false;
    });
  }

  compareTaskGrades(g1?: SchoolGrade, g2?: SchoolGrade): boolean {
    return g1 == g2;
  }

  loadTaskGrades = (): Observable<SchoolGrade[]> => {
    return of([
      SchoolGrade.second, SchoolGrade.third, SchoolGrade.forth, SchoolGrade.fifth, SchoolGrade.sixth,
      SchoolGrade.seventh, SchoolGrade.eighth, SchoolGrade.ninth, SchoolGrade.tenth, SchoolGrade.eleventh
    ]);
  };

  displayTaskGrade(grade: SchoolGrade): string {
    return localizeSchoolGrade(grade);
  }

  changeTaskGrade(grade: SchoolGrade) {
    this.taskGrade = grade;
  }

  canNavigateBack(section: Section, question: Question): boolean {
    return !question.answerInProgress
      && (section.questions.indexOf(question) > 0 || this.taskSections.indexOf(section) > 0);
  }

  canNavigateForward(section: Section, question: Question): boolean {
    return !question.answerInProgress
      && (section.questions.indexOf(question) < (section.questions.length - 1)
        || this.taskSections.indexOf(section) < this.taskSections.length)
      && this.answeredQuestions.has(question.id as string);
  }

  handleClientTimeShift(serverTime: Date) {
    const data: TimeData = {serverTime};
    this.dialog.open(ClientTimeShiftComponent, {
      width: '600px',
      data: data
    });
  }

  handleClientTimeShiftUpdated(shift: number) {
    this.clientTimeShift = shift;
    this.cdRef.detectChanges();
  }

  async showAvailableContests() {
    await this.router.navigate([UrlHelper.CONTESTS_HISTORY_PATH]);
  }
}
